import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { ShopSettings } from "@v2/features/settings/workspace/useUpdateShop";
import type { ReploSimpleProject } from "schemas/generated/project";
import type { CurrencyCode } from "schemas/project";

import * as React from "react";

import { Alert } from "@replo/design-system/blocks/alert/Alert";
import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Button } from "@replo/design-system/components/button/Button";
import {
  Combobox,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import { Input } from "@replo/design-system/components/input/Input";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { Loader } from "@v2/components/Loader";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { useCanAddCustomDomainToShop } from "@v2/features/settings/hooks/useCanAddCustomDomainToShop";
import {
  LoadedSettings,
  SettingsLayout,
} from "@v2/features/settings/SettingsCard";
import { DeleteProjectModal } from "@v2/features/settings/shop/modals/DeleteProjectModal";
import { MoveProjectModal } from "@v2/features/settings/shop/modals/MoveProjectModal";
import { useUpdateShop } from "@v2/features/settings/workspace/useUpdateShop";

import "@v2/hooks/subscription";

import { workspaceUtils } from "@trpc/workspace";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { projectRouter, projectUtils } from "@v2/trpc/routes/project";
import { shopMetadataRouter } from "@v2/trpc/routes/shopMetadata";
import { getCurrencySymbol } from "@v2/utils/currency";
import { getProjectLabels } from "@v2/utils/project-labels";
import { formatInTimeZone } from "date-fns-tz";
import { Clock, Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { formatIanaTimezone } from "replo-utils/lib/timezone";
import { ReploError } from "schemas/errors";
import { CURRENCIES } from "schemas/project";

import { AddDomainModal } from "./domains/AddDomainModal";
import { DomainsDisplay } from "./domains/DomainsDisplay";
import useAddDomainModal from "./domains/useAddDomainModal";
import { PaymentMethodSection } from "./PaymentMethodSection";

class FaviconUploadError extends ReploError {}

const ShopSettingsForm: React.FC<{
  project: ReploSimpleProject;
}> = ({ project }) => {
  const { updateShop, isUpdating } = useUpdateShop();
  const {
    handleSubmit,
    setValue,
    formState: { isDirty },
    watch,
    reset,
  } = useForm<ShopSettings>({
    defaultValues: {
      name: project.name ?? "",
      timeZone:
        project.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      currency: project.currency ?? "USD",
    },
  });
  const labels = getProjectLabels(project.hostingType);

  const handleSetValue = (
    key: keyof ShopSettings,
    value: ShopSettings[keyof ShopSettings],
  ) => {
    setValue(key, value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleShopUpdate = async (shopSettings: ShopSettings) => {
    await updateShop({
      id: project.id,
      data: shopSettings,
    });
    reset({}, { keepValues: true });
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <ShopNameInput
        label={`${labels.singularCapitalized} Name`}
        initialName={watch("name") ?? ""}
        onChange={(value) => handleSetValue("name", value)}
        placeholder={`Enter your ${labels.singular} name`}
      />
      <ShopTimeZoneSettings
        timeZone={watch("timeZone") ?? ""}
        onChange={(value) => handleSetValue("timeZone", value)}
      />
      <ShopCurrencySettings
        currency={watch("currency") ?? "USD"}
        onChange={(value) => handleSetValue("currency", value)}
      />
      <div className="flex w-full flex-row justify-end">
        <Button
          variant="primary"
          size="base"
          onClick={() => void handleSubmit(handleShopUpdate)()}
          loading={isUpdating}
          disabled={!isDirty}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

const ShopTimeZoneSettings: React.FC<{
  timeZone: string;
  onChange: (value: string) => void;
}> = ({ timeZone, onChange }) => {
  const getTimeZoneLabel = (timezone: string) => {
    if (timezone === "UTC") {
      return "UTC (GMT±00:00)";
    }

    const formattedTimezone = formatIanaTimezone(timezone);

    // NOTE (Kurt, 2025-03-03): We format the GMT part to be more readable by
    // replacing GMT+0:00 or GMT-0:00 with GMT±0:00.
    let gmtPart = formatInTimeZone(new Date(), timezone, "'GMT'xxx");
    gmtPart = gmtPart.replace(/GMT[+-]00:00/g, "GMT±00:00");

    return `${formattedTimezone} (${gmtPart})`;
  };

  const timezoneOptions = Intl.supportedValuesOf("timeZone").map(
    (timezone) => ({
      label: getTimeZoneLabel(timezone),
      value: timezone,
    }),
  );

  timezoneOptions.unshift({
    label: getTimeZoneLabel("UTC"),
    value: "UTC",
  });

  const items: ComboboxItemWithValue[] = timezoneOptions.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));

  const selectedItem = items.find((item) => item.value === timeZone) ?? null;

  return (
    <LabeledControl
      label="Timezone"
      description="All analytics will be reported using this timezone."
      size="base"
    >
      <Combobox
        items={items}
        value={selectedItem}
        onValueChange={(item) => {
          if (item) {
            onChange(item.value);
          }
        }}
        isItemEqualToValue={(a, b) => a.value === b.value}
        trigger={
          <ComboboxTriggerButton
            size="base"
            startEnhancer={<Clock size={14} />}
            isPlaceholder={!Boolean(timeZone)}
            className="w-full"
          >
            {getTimeZoneLabel(timeZone ?? "")}
          </ComboboxTriggerButton>
        }
        triggerAsChild
        placeholder="Search..."
      />
    </LabeledControl>
  );
};

const ShopCurrencySettings: React.FC<{
  currency: CurrencyCode;
  onChange: (value: string) => void;
}> = ({ currency, onChange }) => {
  const formatCurrencyLabel = (code: CurrencyCode, name: string) => {
    const symbol = getCurrencySymbol(code);
    // NOTE (Kurt, 2025-04-02): Some currency symbols aren't rendered consistently in the browser, so the
    // Intl API returns the currency code instead, if we get a code back we don't want to show it since we
    // already show the currency code in the label.
    return `${code} - ${name}${symbol !== code ? ` (${symbol})` : ""}`;
  };

  const currencyOptions = Object.entries(CURRENCIES).map(
    ([code, { name }]) => ({
      label: formatCurrencyLabel(code as CurrencyCode, name),
      value: code,
    }),
  );

  const items: ComboboxItemWithValue[] = currencyOptions.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));

  const selectedItem = items.find((item) => item.value === currency) ?? null;

  return (
    <LabeledControl
      label="Currency"
      description="All analytics will be reported in this currency."
      size="base"
    >
      <Combobox
        items={items}
        value={selectedItem}
        onValueChange={(item) => {
          if (item) {
            onChange(item.value);
          }
        }}
        isItemEqualToValue={(a, b) => a.value === b.value}
        trigger={
          <ComboboxTriggerButton
            size="base"
            isPlaceholder={!Boolean(currency)}
            className="w-full"
          >
            {formatCurrencyLabel(currency, CURRENCIES[currency].name)}
          </ComboboxTriggerButton>
        }
        triggerAsChild
        placeholder="Search..."
      />
    </LabeledControl>
  );
};

const ShopNameInput = ({
  label,
  placeholder,
  initialName,
  onChange,
}: {
  label: string;
  placeholder: string;
  initialName: string;
  onChange: (value: string) => void;
}) => {
  return (
    <LabeledControl label={label} size="base">
      <Input
        value={initialName}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        size="base"
      />
    </LabeledControl>
  );
};

type ShopSectionProps = {
  project: ReploSimpleProject;
};

const ShopMoveSection: React.FC<ShopSectionProps & { disabled: boolean }> = ({
  project,
  disabled,
}) => {
  const [moveModalOpen, setMoveModalOpen] = React.useState(false);
  const labels = getProjectLabels(project.hostingType);
  const moveLabel = `Move ${labels.singularCapitalized}`;

  return (
    <div className="flex w-full flex-row justify-end">
      <Button
        variant="primary"
        size="base"
        disabled={disabled}
        onClick={() => {
          if (project.ownerWorkspace?.id) {
            setMoveModalOpen(true);
          }
        }}
      >
        {moveLabel}
      </Button>
      {project.ownerWorkspace?.id && (
        <MoveProjectModal
          open={moveModalOpen}
          onOpenChange={setMoveModalOpen}
          projectId={project.id}
          oldWorkspaceId={project.ownerWorkspace.id}
          shopifyUrl={project.integrations?.shopify?.store.shopifyUrl}
          hostingType={project.hostingType}
        />
      )}
    </div>
  );
};

const ShopDeleteSection: React.FC<ShopSectionProps & { disabled: boolean }> = ({
  project,
  disabled,
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const labels = getProjectLabels(project.hostingType);

  return (
    <div className="flex flex-col gap-4">
      <Alert
        variant="critical"
        description={`Deleting this ${labels.singular} will erase it. You will not be able to recover it without the help of the Replo support team.`}
      />
      <div className="flex w-full flex-row justify-end">
        <Button
          variant="destructive"
          size="base"
          disabled={disabled}
          onClick={() => {
            setDeleteModalOpen(true);
          }}
        >
          Delete {labels.singularCapitalized}
        </Button>
      </div>
      <DeleteProjectModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        projectId={project.id}
        from="list"
      />
    </div>
  );
};

const ShopFaviconSection: React.FC<ShopSectionProps> = () => {
  const logEvent = useLogAnalytics();
  const [lightThemeIcon, setLightThemeIcon] = React.useState<string | null>(
    null,
  );
  const [darkThemeIcon, setDarkThemeIcon] = React.useState<string | null>(null);
  const [isLightUploadLoading, setIsLightUploadLoading] = React.useState(false);
  const [isDarkUploadLoading, setIsDarkUploadLoading] = React.useState(false);
  const lightFileInputRef = React.useRef<HTMLInputElement>(null);
  const darkFileInputRef = React.useRef<HTMLInputElement>(null);

  const { project } = useCurrentProjectContext();

  const { mutate: updateProject } = projectRouter.update.useMutation({
    onSuccess: () => {
      void projectUtils.getV2.invalidate();
    },
  });

  const { mutateAsync: generateSignedUrlForFavicon } =
    shopMetadataRouter.generateSignedUrlForFavicon.useMutation();

  React.useEffect(() => {
    return () => {
      if (lightThemeIcon) {
        URL.revokeObjectURL(lightThemeIcon);
      }
      if (darkThemeIcon) {
        URL.revokeObjectURL(darkThemeIcon);
      }
    };
  }, [lightThemeIcon, darkThemeIcon]);

  if (!project) {
    return null;
  }

  const handleFileUpload = async (
    file: File,
    setIcon: (url: string) => void,
    faviconStyle: "light-mode" | "dark-mode",
    setIsLoading: (loading: boolean) => void,
  ) => {
    const isValidFile =
      file.type.startsWith("image/") ||
      /\.(ico|png|gif|jpe?g)$/i.test(file.name);

    if (!isValidFile) {
      return;
    }

    try {
      setIsLoading(true);

      const objectUrl = URL.createObjectURL(file);
      setIcon(objectUrl);

      const { signedUrl, publicUrl } = await generateSignedUrlForFavicon({
        projectId: project.id,
        faviconStyle: faviconStyle,
        contentType: file.type,
        contentLength: file.size,
      });

      const uploadResponse = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
          "Content-Length": file.size.toString(),
        },
      });

      if (!uploadResponse.ok) {
        throw new FaviconUploadError({
          message: `Failed to upload image ${file.name} to Cloudflare`,
          additionalData: { status: uploadResponse.status },
        });
      }

      updateProject({
        projectId: project.id,
        project: {
          ...(faviconStyle === "light-mode"
            ? { shopMetadataLightModeFaviconUrl: publicUrl }
            : { shopMetadataDarkModeFaviconUrl: publicUrl }),
        },
      });

      logEvent("site.settings.favicon.update", {
        faviconStyle,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createUploadHandler = (
    setIcon: (url: string) => void,
    faviconStyle: "light-mode" | "dark-mode",
    setIsLoading: (loading: boolean) => void,
  ) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        void handleFileUpload(file, setIcon, faviconStyle, setIsLoading);
      }
    };
  };

  return (
    <div className="flex gap-3">
      <div className="flex flex-col gap-2 flex-1">
        <div className="relative">
          <img
            src="/images/shops/BrowserLight.png"
            alt="Light theme preview"
            className="w-full object-contain rounded-lg"
          />
          <img
            src={
              lightThemeIcon ||
              project.shopMetadataLightModeFaviconUrl ||
              "/images/shops/DarkLogo.png"
            }
            alt="Dark logo overlay"
            className="absolute object-contain"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(+165%, -165%)",
              width: "28px",
              height: "28px",
            }}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="pl-2 font-semibold text-sm">Light Theme</div>
          <Button
            variant="secondary"
            size="sm"
            UNSAFE_className="mr-2"
            loading={isLightUploadLoading}
            disabled={isLightUploadLoading}
            onClick={() => lightFileInputRef.current?.click()}
          >
            {project.shopMetadataLightModeFaviconUrl ? "Replace" : "Upload"}
          </Button>
          <input
            ref={lightFileInputRef}
            type="file"
            accept=".ico,.png,.gif,.jpg,.jpeg"
            onChange={createUploadHandler(
              setLightThemeIcon,
              "light-mode",
              setIsLightUploadLoading,
            )}
            style={{ display: "none" }}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 flex-1">
        <div className="relative">
          <img
            src="/images/shops/BrowserDark.png"
            alt="Dark theme preview"
            className="w-full object-contain rounded-lg"
          />
          <img
            src={
              darkThemeIcon ||
              project.shopMetadataDarkModeFaviconUrl ||
              "/images/shops/LightLogo.png"
            }
            alt="Light logo overlay"
            className="absolute object-contain"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(+165%, -165%)",
              width: "28px",
              height: "28px",
            }}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="pl-2 font-semibold text-sm">
            Dark Theme{" "}
            <span className="text-placeholder text-xs font-normal">
              Optional
            </span>
          </div>
          <Button
            variant="secondary"
            size="sm"
            UNSAFE_className="mr-2"
            loading={isDarkUploadLoading}
            disabled={isDarkUploadLoading}
            onClick={() => darkFileInputRef.current?.click()}
          >
            {project.shopMetadataDarkModeFaviconUrl ? "Replace" : "Upload"}
          </Button>
          <input
            ref={darkFileInputRef}
            type="file"
            accept=".ico,.png,.gif,.jpg,.jpeg"
            onChange={createUploadHandler(
              setDarkThemeIcon,
              "dark-mode",
              setIsDarkUploadLoading,
            )}
            style={{ display: "none" }}
          />
        </div>
      </div>
    </div>
  );
};

const getGeneralSettingsTitle = (hostingType: "replo" | "shopify") => {
  const labels = getProjectLabels(hostingType);
  return `General ${labels.singularCapitalized} Settings`;
};

const ShopSettingsWrapper = () => {
  const { project, isLoading: isLoadingProject } = useCurrentProjectContext();
  const navigate = useNavigate();
  const {
    addDomainModalOpen,
    setAddDomainModalOpen,
    openAddDomainModal,
    isEntriModalInProgress,
  } = useAddDomainModal();

  const { canAdd: canAddCustomDomain, hasPaidSubscription } =
    useCanAddCustomDomainToShop({
      projectId: project?.id ?? undefined,
    });

  const { userRole } = useCurrentProjectContext();
  const canDeleteOrMoveShop = exhaustiveSwitch({ type: userRole ?? "none" })({
    superuser: () => true,
    workspaceOwner: () => true,
    workspaceMember: () => false,
    projectMember: () => false,
    none: () => false,
  });

  if (!project) {
    return null;
  }

  const showReploHostedSections = project.hostingType === "replo";
  const showPaymentMethodSection = project.hostingType === "replo";

  // NOTE (Cole, 2026-02-13, REPL-23837): Checks the custom domain entitlement.
  // If the entitlement is exceeded, the tRPC error handler shows a toast with
  // an upgrade CTA.
  const handleAddDomain = async () => {
    if (!project.shopDomainPrefix) {
      return;
    }
    await workspaceUtils.throwIfCannotCreateCustomDomain.fetch({
      projectId: project.id,
      workspaceId: project.ownerWorkspace?.id ?? "",
    });
    openAddDomainModal();
  };

  const sections = [
    ...(showPaymentMethodSection
      ? [
          {
            title: "Commerce Platform",
            description: "How you manage products and get paid",
            component: <PaymentMethodSection project={project} />,
          },
        ]
      : []),
    {
      title: getGeneralSettingsTitle(project.hostingType),
      component: <ShopSettingsForm project={project} />,
    },
    ...(showReploHostedSections
      ? [
          {
            title: "Domains",
            actions: (
              <div className="flex gap-2">
                {!canAddCustomDomain && !hasPaidSubscription && (
                  <Button
                    variant="link"
                    size="base"
                    onClick={() => {
                      analytics.logEvent(
                        "billing.customDomainsUpgradePlanCta.clicked",
                        {},
                      );
                      navigate(
                        `/shops/${project.id}/settings/workspace/billing`,
                      );
                    }}
                  >
                    Upgrade your plan
                  </Button>
                )}
                <Tooltip
                  content={
                    !canAddCustomDomain &&
                    !hasPaidSubscription &&
                    "Upgrade to a paid plan to add a custom domain"
                  }
                  triggerAsChild
                >
                  <Button
                    variant="secondary"
                    size="base"
                    startEnhancer={<Plus />}
                    onClick={() => void handleAddDomain()}
                    disabled={
                      (!canAddCustomDomain && !hasPaidSubscription) ||
                      !project.shopDomainPrefix
                    }
                  >
                    Add Domain
                  </Button>
                </Tooltip>
              </div>
            ),
            component: (
              <DomainsDisplay
                projectId={project.id}
                shopDomainPrefix={project.shopDomainPrefix}
              />
            ),
          },
        ]
      : []),
    ...(showReploHostedSections
      ? [
          {
            title: "Site Favicon",
            description:
              "Upload a 64x64 pixel ICO, PNG, GIF, or JPG to display in browser tabs.",
            component: <ShopFaviconSection project={project} />,
          },
        ]
      : []),
    ...(canDeleteOrMoveShop
      ? (() => {
          const sectionLabels = getProjectLabels(project.hostingType);
          return [
            {
              title: `Move ${sectionLabels.singularCapitalized}`,
              description: `Move this ${sectionLabels.singular} to a different workspace.`,
              component: <ShopMoveSection project={project} disabled={false} />,
            },
            {
              title: `Delete ${sectionLabels.singularCapitalized}`,
              description: `Remove this ${sectionLabels.singular} from your workspace.`,
              component: (
                <ShopDeleteSection project={project} disabled={false} />
              ),
            },
          ];
        })()
      : []),
  ];

  return (
    <>
      <SettingsLayout>
        {isLoadingProject ? <Loader /> : <LoadedSettings sections={sections} />}
      </SettingsLayout>
      {project.shopDomainPrefix && (
        <AddDomainModal
          open={addDomainModalOpen}
          onOpenChange={setAddDomainModalOpen}
          projectId={project.id}
          shopDomainPrefix={project.shopDomainPrefix}
          isEntriModalInProgress={isEntriModalInProgress}
        />
      )}
    </>
  );
};

export default ShopSettingsWrapper;
